import { API_URL } from '../config/endpoints';

/**
 * Typed fetchers for the admin API (specs/005-admin-dashboard/contracts/admin-api.md),
 * plus the rider-report review endpoints (/api/admin/reports).
 *
 * They never throw: every outcome is a value the UI can render. The token is
 * passed in by the caller (from AuthContext's `token`) so these stay plain
 * functions that can be tested without React.
 */

export type AdminError =
  | { kind: 'signin' }
  | { kind: 'forbidden' }
  | { kind: 'slow'; retryAfter: number | null }
  | { kind: 'notfound' }
  /** 409: someone else already acted on it. */
  | { kind: 'conflict' }
  | { kind: 'unavailable' };

export type AdminResult<T> = { ok: true; data: T } | { ok: false; error: AdminError };

export type EngineState = 'running' | 'stalled' | 'stopped';

export interface LiveStatus {
  engine: { state: EngineState; reasons: string[]; lastProgressAt: string | null; stallThresholdMs: number };
  phase: 'STARTING' | 'LAUNCHING' | 'IN_PROGRESS' | 'CRASHED' | null;
  gameNumber: number | null;
  lastRoundEndedAt: string | null;
  roundsLastHour: number;
  playersConnected: number;
  betsThisRound: number;
  serverTime: string;
}

export type OverviewRange = '7d' | '30d' | '90d';

export interface Overview {
  range: { days?: number; from: string | null; to: string };
  dau: Array<{ day: string; players: number }>;
  wau: number;
  mau: number;
  newPlayers: number;
  retention: { cohort?: number; d1: number; d7: number };
  rounds: number;
  wagered: string;
  paidOut: string;
  realisedRtp: number | null;
  autoCashoutShare: number;
  platforms: Record<string, number>;
  expectedRtp?: number;
  includesSolo?: boolean;
  timezone?: string;
  generatedAt?: string;
}

export type PlayerSort = 'lastSeen' | 'rounds' | 'net';

export interface PlayerQuery {
  q: string;
  sort: PlayerSort;
  page: number;
  pageSize: number;
}

export interface PlayerSummary {
  id: string;
  username: string;
  platform: string | null;
  lastSeenAt: string | null;
  roundsPlayed: number;
  net: string;
}

export interface PlayerList {
  items: PlayerSummary[];
  total: number;
}

export interface PlayerStats {
  roundsPlayed: number; busts: number; cashouts: number; cashoutRate: number;
  wagered: string; paidOut: string; net: string; avgBet: string;
  avgCashoutMultiplier: number | null; autoCashoutShare: number;
  biggestWin: string; biggestLoss: string;
  firstPlayedAt: string | null; lastPlayedAt: string | null;
  daysPlayed: number; currentStreakDays: number; longestStreakDays: number;
  sessions: number; avgSessionMinutes: number;
}

export interface RecentBet {
  id: string;
  gameNumber: number | null;
  amount: string;
  status: string;
  cashoutMultiplier: number | null;
  payout: string;
  profit: string;
  placedAt: string;
}

export interface PlayerProfileData {
  profile: {
    id: string;
    username: string;
    displayName: string | null;
    emailMasked: string | null;
    providers: string[];
    role: string;
    isActive: boolean;
    createdAt: string | null;
    lastSeenAt?: string | null;
    deletedAt?: string | null;
  };
  sessions: { count: number; avgMinutes: number; lastSeenAt?: string | null; lastPlatform: string | null; lastAppVersion: string | null };
  stats: PlayerStats;
  recentBets: RecentBet[];
}

export type ReportStatus = 'open' | 'resolved';
export type ReportReason = 'offensive_name' | 'cheating' | 'other';
export type ReportAction = 'dismiss' | 'reset_name';

/** One player report about a rider. Names are as players see them; no account ids. */
export interface RiderReport {
  id: string;
  riderId: string;
  reason: ReportReason;
  note: string | null;
  status: ReportStatus;
  action: ReportAction | 'account_closed' | null;
  createdAt: string | null;
  resolvedAt: string | null;
  /** The name players were shown when the report was made. */
  nameAtReport: string | null;
  /** The name players are shown now. */
  currentName: string;
  /** False once the rider is back on the generated name: nothing left to reset. */
  hasCustomName: boolean;
  /** Open reports about this rider, this one included. */
  openReports: number;
  accountClosed: boolean;
}

export interface ReportList {
  items: RiderReport[];
}

export interface ResolveResult {
  ok: true;
  /** Reports closed by this decision (a name reset also closes other open name reports). */
  resolved: number;
}

function errorFor(response: Response): AdminError {
  switch (response.status) {
    case 401: return { kind: 'signin' };
    case 403: return { kind: 'forbidden' };
    case 404: return { kind: 'notfound' };
    case 409: return { kind: 'conflict' };
    case 429: {
      const header = Number(response.headers?.get('retry-after'));
      return { kind: 'slow', retryAfter: Number.isFinite(header) && header > 0 ? header : null };
    }
    default: return { kind: 'unavailable' };
  }
}

async function request<T>(path: string, token: string | null, signal?: AbortSignal, body?: unknown): Promise<AdminResult<T>> {
  if (!token) return { ok: false, error: { kind: 'signin' } };
  try {
    const response = await fetch(`${API_URL}${path}`, body === undefined
      ? { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store', signal }
      : {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        cache: 'no-store',
        signal,
      });
    if (!response.ok) return { ok: false, error: errorFor(response) };
    return { ok: true, data: (await response.json()) as T };
  } catch {
    return { ok: false, error: { kind: 'unavailable' } };
  }
}

export const getLive = (token: string | null, signal?: AbortSignal) =>
  request<LiveStatus>('/api/admin/live', token, signal);

export const getOverview = (token: string | null, range: OverviewRange, signal?: AbortSignal) =>
  request<Overview>(`/api/admin/overview?range=${encodeURIComponent(range)}`, token, signal);

export const getPlayers = (token: string | null, query: PlayerQuery, signal?: AbortSignal) => {
  const params = new URLSearchParams({
    q: query.q, sort: query.sort, page: String(query.page), pageSize: String(query.pageSize),
  });
  return request<PlayerList>(`/api/admin/players?${params.toString()}`, token, signal);
};

export const getPlayer = (token: string | null, id: string, signal?: AbortSignal) =>
  request<PlayerProfileData>(`/api/admin/players/${encodeURIComponent(id)}`, token, signal);

export const getReports = (token: string | null, status: ReportStatus, signal?: AbortSignal) =>
  request<ReportList>(`/api/admin/reports?status=${encodeURIComponent(status)}`, token, signal);

export const resolveReport = (token: string | null, id: string, action: ReportAction) =>
  request<ResolveResult>(`/api/admin/reports/${encodeURIComponent(id)}/resolve`, token, undefined, { action });
